// 💡 CLARA INVESTMENT COCKPIT - TRANSPARENZ-SYSTEM
// Info-Tooltips und Berechnungsdetails für maximale Transparenz

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Info, 
  X, 
  Calculator, 
  BookOpen, 
  TrendingUp, 
  AlertCircle,
  CheckCircle,
  HelpCircle
} from 'lucide-react';

const TransparencyTooltip = ({ isOpen, onClose, kpiInfo, calculationData, kpis }) => {
  if (!isOpen || !kpiInfo) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-blue-600" />
            Berechnungsdetails
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Formel-Sektion */}
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Calculator className="h-4 w-4 text-blue-600" />
              <h3 className="font-semibold text-blue-900 dark:text-blue-100">Formel</h3>
            </div>
            <code className="text-lg font-mono bg-white dark:bg-gray-800 px-3 py-2 rounded border block">
              {kpiInfo.formula}
            </code>
          </div>

          {/* Erklärung */}
          <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="h-4 w-4 text-green-600" />
              <h3 className="font-semibold text-green-900 dark:text-green-100">Erklärung</h3>
            </div>
            <p className="text-green-800 dark:text-green-200">
              {kpiInfo.explanation}
            </p>
          </div>

          {/* Beispielrechnung */}
          <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-4 w-4 text-purple-600" />
              <h3 className="font-semibold text-purple-900 dark:text-purple-100">Ihre Berechnung</h3>
            </div>
            <code className="text-sm font-mono bg-white dark:bg-gray-800 px-3 py-2 rounded border block">
              {kpiInfo.example}
            </code>
          </div>

          {/* Bewertung */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="h-4 w-4 text-yellow-600" />
              <h3 className="font-semibold text-yellow-900 dark:text-yellow-100">Was ist ein guter Wert?</h3>
            </div>
            <p className="text-yellow-800 dark:text-yellow-200">
              {kpiInfo.goodValue}
            </p>
          </div>

          {/* Zusätzliche Insights */}
          {getAdditionalInsights(kpiInfo, calculationData, kpis) && (
            <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="h-4 w-4 text-orange-600" />
                <h3 className="font-semibold text-orange-900 dark:text-orange-100">Zusätzliche Insights</h3>
              </div>
              <div className="space-y-2">
                {getAdditionalInsights(kpiInfo, calculationData, kpis).map((insight, index) => (
                  <p key={index} className="text-orange-800 dark:text-orange-200 text-sm">
                    • {insight}
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* Verbesserungsvorschläge */}
          {getImprovementSuggestions(kpiInfo, calculationData, kpis) && (
            <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <HelpCircle className="h-4 w-4 text-indigo-600" />
                <h3 className="font-semibold text-indigo-900 dark:text-indigo-100">Verbesserungsvorschläge</h3>
              </div>
              <div className="space-y-2">
                {getImprovementSuggestions(kpiInfo, calculationData, kpis).map((suggestion, index) => (
                  <p key={index} className="text-indigo-800 dark:text-indigo-200 text-sm">
                    💡 {suggestion}
                  </p>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Hilfsfunktion für zusätzliche Insights
const getAdditionalInsights = (kpiInfo, calculationData, kpis) => {
  const insights = [];
  
  if (!calculationData || !kpis) return insights;

  // Spezifische Insights basierend auf KPI-Typ
  if (kpiInfo.formula.includes('Jahresmiete')) {
    // Rendite-bezogene Insights
    if (kpis.grossRentalYield > 10) {
      insights.push('Sehr hohe Rendite - prüfen Sie die Nachhaltigkeit und mögliche Risiken');
    }
    if (kpis.grossRentalYield < 3) {
      insights.push('Niedrige Rendite - eventuell in Premium-Lage mit Wertsteigerungspotenzial');
    }
    
    const rentPerSqm = kpis.rentPerSqm;
    if (rentPerSqm > 15) {
      insights.push(`Hohe Miete von €${rentPerSqm.toFixed(2)}/m² - Marktkonformität prüfen`);
    }
    if (rentPerSqm < 8) {
      insights.push(`Niedrige Miete von €${rentPerSqm.toFixed(2)}/m² - Mieterhöhungspotenzial vorhanden`);
    }
  }

  if (kpiInfo.formula.includes('Eigenkapital')) {
    // Eigenkapital-bezogene Insights
    const equityRatio = kpis.equityPercent;
    if (equityRatio < 20) {
      insights.push('Niedrige Eigenkapitalquote - höhere Finanzierungskosten möglich');
    }
    if (equityRatio > 50) {
      insights.push('Hohe Eigenkapitalquote - eventuell Kapital für weitere Investments nutzen');
    }
  }

  if (kpiInfo.formula.includes('Cashflow')) {
    // Cashflow-bezogene Insights
    if (kpis.monthlyCashflowAfterTax < 0) {
      insights.push('Negativer Cashflow - Investment benötigt monatliche Zuschüsse');
    }
    if (kpis.monthlyCashflowAfterTax > 500) {
      insights.push('Sehr guter Cashflow - selbsttragendes Investment mit Überschuss');
    }
    
    // Steuereffekt-Analyse
    if (kpis.taxEffect < 0) {
      insights.push(`Steuerersparnis von €${Math.abs(kpis.taxEffect).toLocaleString()}/Jahr durch Verluste`);
    }
    if (kpis.taxEffect > 5000) {
      insights.push(`Hohe Steuerlast von €${kpis.taxEffect.toLocaleString()}/Jahr - Optimierung prüfen`);
    }
  }

  return insights.length > 0 ? insights : null;
};

// Hilfsfunktion für Verbesserungsvorschläge
const getImprovementSuggestions = (kpiInfo, calculationData, kpis) => {
  const suggestions = [];
  
  if (!calculationData || !kpis) return suggestions;

  // Rendite-Verbesserungen
  if (kpis.grossRentalYield < 5) {
    suggestions.push('Mieterhöhung prüfen - eventuell unter Marktmiete');
    suggestions.push('Nebenkosten-Optimierung durch Energieeffizienz-Maßnahmen');
  }

  // Cashflow-Verbesserungen
  if (kpis.monthlyCashflowAfterTax < 0) {
    suggestions.push('Refinanzierung bei besseren Konditionen prüfen');
    suggestions.push('Betriebskosten analysieren und optimieren');
    suggestions.push('Mieterhöhung entsprechend Mietspiegel durchführen');
  }

  // Eigenkapitalrendite-Verbesserungen
  if (kpis.equityReturn < 6) {
    suggestions.push('Fremdkapital-Anteil erhöhen für Leverage-Effekt');
    suggestions.push('Sanierungsmaßnahmen für höhere Mieten prüfen');
  }

  // Steuer-Optimierungen
  if (kpis.taxEffect > 3000) {
    suggestions.push('Sonderabschreibungen (z.B. Denkmalschutz) prüfen');
    suggestions.push('Modernisierungsmaßnahmen steuerlich absetzen');
  }

  // Finanzierungs-Optimierungen
  const totalLoanAmount = (calculationData.loan1_amount || 0) + (calculationData.loan2_amount || 0) + (calculationData.loan3_amount || 0);
  const avgInterestRate = totalLoanAmount > 0 ? 
    ((calculationData.loan1_amount || 0) * (calculationData.loan1_interest_rate || 0) +
     (calculationData.loan2_amount || 0) * (calculationData.loan2_interest_rate || 0) +
     (calculationData.loan3_amount || 0) * (calculationData.loan3_interest_rate || 0)) / totalLoanAmount : 0;

  if (avgInterestRate > 4.5) {
    suggestions.push('Umschuldung prüfen - aktuell niedrigere Zinsen verfügbar');
  }

  return suggestions.length > 0 ? suggestions : null;
};

// Kompakte Tooltip-Komponente für Hover-Effekte
export const QuickTooltip = ({ children, content, className = "" }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className={className}
      >
        {children}
      </div>
      
      {isVisible && content && (
        <div className="absolute z-50 px-3 py-2 text-sm bg-gray-900 text-white rounded-lg shadow-lg -top-2 left-full ml-2 whitespace-nowrap max-w-xs">
          {content}
          <div className="absolute top-1/2 -left-1 transform -translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
        </div>
      )}
    </div>
  );
};

// Info-Button-Komponente
export const InfoButton = ({ onClick, className = "" }) => {
  return (
    <button
      onClick={onClick}
      className={`p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors ${className}`}
      title="Berechnungsdetails anzeigen"
    >
      <Info className="h-4 w-4 text-gray-400 hover:text-blue-600" />
    </button>
  );
};

// Formel-Anzeige-Komponente
export const FormulaDisplay = ({ formula, className = "" }) => {
  return (
    <div className={`bg-gray-100 dark:bg-gray-800 p-2 rounded font-mono text-sm ${className}`}>
      {formula}
    </div>
  );
};

// Bewertungs-Badge-Komponente
export const ValueBadge = ({ value, thresholds, labels, className = "" }) => {
  const getBadgeColor = () => {
    if (value >= thresholds.excellent) return 'bg-green-100 text-green-800';
    if (value >= thresholds.good) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getBadgeLabel = () => {
    if (value >= thresholds.excellent) return labels.excellent;
    if (value >= thresholds.good) return labels.good;
    return labels.poor;
  };

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getBadgeColor()} ${className}`}>
      {getBadgeLabel()}
    </span>
  );
};

// Hauptexport
export default TransparencyTooltip;

